import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Download, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Header } from '@/components/layout/Header';
import { format } from 'date-fns';

interface ParticipantData {
  id: string;
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  registration_date: string;
  registration_status: string;
  checked_in_at: string | null;
}

const EventParticipants = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState<any>(null);
  const [participants, setParticipants] = useState<ParticipantData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id, user]);

  const loadData = async () => {
    if (!user) return;
    setLoading(true);

    try {
      // Load event data
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('id', id)
        .eq('organizer_id', user.id)
        .single();

      if (eventError) throw eventError;
      setEvent(eventData);

      // Load participants via edge function
      const { data, error } = await supabase.functions.invoke('get-event-participants', {
        body: { event_id: id }
      });

      if (error) throw error;
      setParticipants(data?.participants || []);
    } catch (error: any) {
      console.error('Error loading participants:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async (registrationId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('event_registrations')
        .update({
          registration_status: currentStatus ? 'registered' : 'attended',
          checked_in_at: currentStatus ? null : new Date().toISOString(),
        })
        .eq('id', registrationId);

      if (error) throw error;

      toast({
        title: currentStatus ? t('events.checkinRemoved') : t('events.checkinSuccess'),
      });

      loadData();
    } catch (error: any) {
      console.error('Error updating check-in:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    }
  };

  const getParticipantName = (participant: ParticipantData) => {
    const firstName = participant.first_name || '';
    const lastName = participant.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || 'N/A';
  };

  const exportCSV = () => {
    const csv = [
      ['Nome', 'Cognome', 'Email', 'Status', 'Data Registrazione', 'Check-in'].join(','),
      ...participants.map((p) =>
        [
          p.first_name || '',
          p.last_name || '',
          p.email,
          p.registration_status,
          format(new Date(p.registration_date), 'PPp'),
          p.checked_in_at ? format(new Date(p.checked_in_at), 'PPp') : '-',
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `participants-${event?.slug}.csv`;
    a.click();
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      registered: 'default',
      waitlist: 'secondary',
      cancelled: 'destructive',
      attended: 'default',
    };
    return <Badge variant={variants[status]}>{t(`events.status.${status}`)}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <p>{t('common.loading')}</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate('/dashboard/events')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">{event?.title}</h1>
            <p className="text-muted-foreground">
              {participants.length} {t('events.participants')}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportCSV}>
              <Download className="w-4 h-4 mr-2" />
              {t('events.exportCsv')}
            </Button>
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('events.participantName', 'Nome')}</TableHead>
                <TableHead>{t('events.participantEmail', 'Email')}</TableHead>
                <TableHead>{t('events.registrationDate')}</TableHead>
                <TableHead>{t('events.status')}</TableHead>
                <TableHead>{t('events.checkIn')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {participants.map((participant) => (
                <TableRow key={participant.id}>
                  <TableCell className="font-medium">
                    {getParticipantName(participant)}
                  </TableCell>
                  <TableCell>{participant.email}</TableCell>
                  <TableCell>
                    {format(new Date(participant.registration_date), 'PPp')}
                  </TableCell>
                  <TableCell>{getStatusBadge(participant.registration_status)}</TableCell>
                  <TableCell>
                    {participant.registration_status === 'cancelled' ? (
                      <Badge variant="outline">{t('events.cancelled')}</Badge>
                    ) : participant.checked_in_at ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm">
                          {format(new Date(participant.checked_in_at), 'PPp')}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            handleCheckIn(participant.id, true)
                          }
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleCheckIn(participant.id, false)}
                      >
                        {t('events.checkIn')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
};

export default EventParticipants;
